import { Edit3, Plus, Save, Sparkles, UserCog, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "../../components/EmptyState.jsx";
import { Field, Toggle } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { createLoginUserProfile } from "../../firebase/auth.js";
import { DEFAULT_SETTINGS, getSettings, getUsers, saveSettings, saveUserProfile, seedSampleMasters } from "../../firebase/firestore.js";

const emptyUser = {
  uid: "",
  name: "",
  email: "",
  password: "",
  role: "user",
  active: true
};

export default function SettingsPage() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [userModal, setUserModal] = useState(null);
  const { showToast } = useToast();

  async function load() {
    setLoading(true);
    try {
      const [nextSettings, nextUsers] = await Promise.all([getSettings(), getUsers()]);
      setSettings(nextSettings);
      setUsers(nextUsers);
    } catch (error) {
      showToast(error.message || "Unable to load settings", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSaveSettings(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await saveSettings(settingsModal);
      setSettings(settingsModal);
      setSettingsModal(false);
      showToast("Settings saved");
    } catch (error) {
      showToast(error.message || "Unable to save settings", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveUser(event) {
    event.preventDefault();
    setSaving(true);
    try {
      if (userModal.isNew) {
        await createLoginUserProfile({
          name: userModal.form.name,
          email: userModal.form.email,
          password: userModal.form.password,
          role: userModal.form.role,
          active: userModal.form.active
        });
        showToast("Login user created");
      } else {
        await saveUserProfile(userModal.form.uid, {
          name: userModal.form.name,
          email: userModal.form.email,
          role: userModal.form.role,
          active: userModal.form.active
        });
        showToast("User profile saved");
      }
      setUserModal(null);
      await load();
    } catch (error) {
      showToast(error.message || "Unable to save user", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleSeed() {
    setSaving(true);
    try {
      const created = await seedSampleMasters();
      showToast(created ? "Sample master data created" : "Master data already exists", created ? "success" : "info");
    } catch (error) {
      showToast(error.message || "Unable to create sample data", "error");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <section className="admin-page">
        <SkeletonList />
      </section>
    );
  }

  return (
    <section className="admin-page">
      <div className="page-title-row">
        <div>
          <p>Admin</p>
          <h2>Settings</h2>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => setSettingsModal(settings)}>
          <Edit3 size={18} />
          Edit Settings
        </button>
      </div>

      <div className="settings-grid">
        <article className="card settings-card">
          <h3>App Details</h3>
          <p><strong>App name:</strong> {settings.appName}</p>
          <p><strong>Institute:</strong> {settings.instituteName}</p>
          <p><strong>Entry grace:</strong> {settings.entryGraceMinutes} minutes</p>
          <p><strong>Exit grace:</strong> {settings.exitGraceMinutes} minutes</p>
        </article>
        <article className="card settings-card">
          <h3>Master Data</h3>
          <p>Manage classes, sections, rooms, subjects, and teachers from the admin menu.</p>
          <div className="link-row">
            <Link className="btn btn-ghost" to="/admin/resources">Classes / Rooms</Link>
            <Link className="btn btn-ghost" to="/admin/subjects">Subjects</Link>
            <Link className="btn btn-ghost" to="/admin/teachers">Teachers</Link>
          </div>
          <button className="btn btn-primary" type="button" onClick={handleSeed} disabled={saving}>
            {saving ? <span className="spinner" /> : <Sparkles size={18} />}
            Create sample data
          </button>
        </article>
      </div>

      <div className="page-title-row sub">
        <div>
          <p>Roles</p>
          <h2>Admin & Normal Users</h2>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => setUserModal({ form: emptyUser, isNew: true })}>
          <Plus size={18} />
          Add Login User
        </button>
      </div>

      {users.length ? (
        <div className="manager-grid">
          {users.map((item) => (
            <article className="card manager-card" key={item.uid || item.id}>
              <div className="avatar-small"><UserCog size={18} /></div>
              <div>
                <p className="eyebrow">{item.email}</p>
                <h3>{item.name || "Unnamed user"}</h3>
                <div className="chip-row">
                  <StatusChip status={item.role === "admin" ? "Admin" : "Normal User"} />
                  <StatusChip status={item.active ? "Active" : "Inactive"} />
                </div>
              </div>
              <button className="icon-button" type="button" onClick={() => setUserModal({ form: { ...emptyUser, ...item, uid: item.uid || item.id }, isNew: false })}>
                <Edit3 size={18} />
              </button>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState title="No users found" text="Add a login user directly from here." />
      )}

      <Modal open={Boolean(settingsModal)} title="App Settings" onClose={() => setSettingsModal(false)}>
        <form className="form-grid" onSubmit={handleSaveSettings}>
          <Field label="App name">
            <input value={settingsModal?.appName || ""} onChange={(event) => setSettingsModal((value) => ({ ...value, appName: event.target.value }))} required />
          </Field>
          <Field label="Institute name">
            <input value={settingsModal?.instituteName || ""} onChange={(event) => setSettingsModal((value) => ({ ...value, instituteName: event.target.value }))} required />
          </Field>
          <Field label="Entry grace minutes">
            <input type="number" min="0" value={settingsModal?.entryGraceMinutes ?? 5} onChange={(event) => setSettingsModal((value) => ({ ...value, entryGraceMinutes: Number(event.target.value) }))} />
          </Field>
          <Field label="Exit grace minutes">
            <input type="number" min="0" value={settingsModal?.exitGraceMinutes ?? 5} onChange={(event) => setSettingsModal((value) => ({ ...value, exitGraceMinutes: Number(event.target.value) }))} />
          </Field>
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setSettingsModal(false)}><X size={17} />Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : <Save size={17} />}Save</button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(userModal)} title={userModal?.isNew ? "Add Login User" : "Edit User Profile"} onClose={() => setUserModal(null)}>
        <form className="form-grid" onSubmit={handleSaveUser}>
          {!userModal?.isNew ? (
            <Field label="Firebase Auth UID">
              <input
                value={userModal?.form.uid || ""}
                onChange={(event) => setUserModal((value) => ({ ...value, form: { ...value.form, uid: event.target.value } }))}
                disabled
                required
              />
            </Field>
          ) : null}
          <Field label="Name">
            <input value={userModal?.form.name || ""} onChange={(event) => setUserModal((value) => ({ ...value, form: { ...value.form, name: event.target.value } }))} required />
          </Field>
          <Field label="Email">
            <input type="email" value={userModal?.form.email || ""} onChange={(event) => setUserModal((value) => ({ ...value, form: { ...value.form, email: event.target.value } }))} required />
          </Field>
          {userModal?.isNew ? (
            <Field label="Password" hint="Minimum 6 characters. User can login with this password.">
              <input
                type="password"
                value={userModal?.form.password || ""}
                onChange={(event) => setUserModal((value) => ({ ...value, form: { ...value.form, password: event.target.value } }))}
                minLength={6}
                required
              />
            </Field>
          ) : null}
          <Field label="Role">
            <select value={userModal?.form.role || "user"} onChange={(event) => setUserModal((value) => ({ ...value, form: { ...value.form, role: event.target.value } }))}>
              <option value="admin">Admin</option>
              <option value="user">Normal User</option>
            </select>
          </Field>
          <Toggle label="Active user" checked={userModal?.form.active !== false} onChange={(value) => setUserModal((item) => ({ ...item, form: { ...item.form, active: value } }))} />
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setUserModal(null)}><X size={17} />Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : <Save size={17} />}{userModal?.isNew ? "Create User" : "Save User"}</button>
          </div>
        </form>
      </Modal>
    </section>
  );
}
